import React from 'react'

import i18n from 'Pods/Shared/I18n'

// File & Scene verifications - using actual API property names
export const getFileVerification = (data, check) => {
  switch (check) {
    case 'fbxFormat':
      return data?.files?.fbxFormat?.pass || false
    default:
      return false
  }
}

export const getSceneVerification = (data, check) => {
  switch (check) {
    case 'dccObjectTypes':
      return data?.scene?.dccObjectTypes?.pass || false
    default:
      return false
  }
}

// Geometry verifications - using actual API property names
export const getGeometryVerification = (data, check) => {
  switch (check) {
    case 'noNgons':
      return data?.geometry?.noNgons?.pass || false
    case 'facetedGeometry':
      return data?.geometry?.facetedGeometry?.pass || false
    case 'nonManifoldVertices':
      return data?.geometry?.nonManifoldVertices?.pass || false
    default:
      return false
  }
}

// Texture & Material verifications - using actual API property names
export const getTextureVerification = (data, check) => {
  switch (check) {
    case 'missingRequiredTextures':
      return data?.texturesAndMaterials?.missingRequiredTextures?.pass || false
    case 'embeddedTexturesFbx':
      return data?.texturesAndMaterials?.embeddedTexturesFbx?.pass || false
    case 'textureAspectRatio':
      return data?.texturesAndMaterials?.textureAspectRatio?.pass || false
    case 'textureResolutionNotPowerOf2':
      return data?.texturesAndMaterials?.textureResolutionNotPowerOf2?.pass || false
    case 'numberOfMaterialsPerObject':
      return data?.texturesAndMaterials?.numberOfMaterialsPerObject?.pass || false
    default:
      return false
  }
}

// UV & Naming verifications - using actual API property names
export const getUVVerification = (data, check) => {
  switch (check) {
    case 'overlappedUvPerUVIsland':
      return data?.uV?.overlappedUvPerUVIsland?.pass || false
    case 'numberOfUVs':
      return data?.uV?.numberOfUVs?.pass || false
    case 'allowedCharacters':
      return data?.naming?.allowedCharacters?.pass || false
    default:
      return false
  }
}

// 3D Print Model verifications - using direct boolean values
export const get3DPrintVerification = (data, check) => {
  switch (check) {
    case 'noOpenEdges':
      return data?.cleanGeometry?.noOpenEdges || false
    case 'manifoldGeometry':
      return data?.cleanGeometry?.manifoldGeometry || false
    case 'noSelfIntersections':
      return data?.cleanGeometry?.noSelfIntersections || false
    case 'noOverlappingFaces':
      return data?.cleanGeometry?.noOverlappingFaces || false
    case 'noZeroAngledCorners':
      return data?.cleanGeometry?.noZeroAngledCorners || false
    case 'noZeroLengthEdges':
      return data?.cleanGeometry?.noZeroLengthEdges || false
    case 'noZeroAreaFaces':
      return data?.cleanGeometry?.noZeroAreaFaces || false
    case 'noBadContiguousEdges':
      return data?.cleanGeometry?.noBadContiguousEdges || false
    default:
      return false
  }
}

// Static geometry tooltip content extracted from YAML
export const getGeometryTooltipContent = (check, locale = 'en') => {
  switch (check) {
    case 'noOpenEdges':
      return <span>Model should be watertight</span>
    case 'openEdges':
      return <span>Model should be watertight</span>
    case 'flippedNormals':
      return (
        <span>
          Surface normals of a 3D model consistently point outward, ensuring proper lighting,
          shading, and rendering. Flipped normals can cause visual artifacts and printing issues.
        </span>
      )
    case 'manifoldGeometry':
      return (
        <span>
          Manifold geometry means all surfaces are properly connected, avoiding issues like edges shared
          by more than two faces.
          <br />
          <a
            href="https://help.cgtrader.com/hc/en-us/articles/12485385944849#h_01GWP89YFDSNN5QRRW6W1A8KGB"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </span>
      )
    case 'noSelfIntersections':
      return <span>
        {i18n.t('itemPage.verifiedByCGTrader.geometryTooltips.noSelfIntersections', { ns: 'rails', lng: locale })}
      </span>
    case 'noOverlappingFaces':
      return <span>Model should not contain overlapping faces</span>
    case 'noZeroAngledCorners':
      return <span>Model should not contain faces with zero-angle corners</span>
    case 'noZeroLengthEdges':
      return <span>Model should not contain zero-length edges</span>
    case 'noZeroAreaFaces':
      return <span>Model should not contain zero-area faces</span>
    case 'noBadContiguousEdges':
      return <span>Model should not have faces with normals pointing in the wrong direction</span>
    default:
      return null
  }
}

// Tooltip content mapping for verification checks
export const getTooltipContent = (category, check, locale = 'en') => {
  switch (category) {
    case 'file':
      switch (check) {
        case 'fbxFormat':
          return (
            <span>
              Binary FBX file is more compact and faster to load and process.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/14118757652881#h_01GWP8R5FAZFB7DW272VWPC70Y"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        default:
          return null
      }

    case 'scene':
      switch (check) {
        case 'dccObjectTypes':
          return (
            <span>
              Unsupported objects:
              <br />- Lights
              <br />- Cameras
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12484392675473#h_01GWP8ESMVEC66RKEHAZ0PQNYS"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        default:
          return null
      }

    case 'geometry':
      switch (check) {
        case 'noNgons':
          return (
            <span>
              N-gons are polygons with five or more sides which might cause issues
              in certain processes like rendering or animation.{' '}
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12485385944849#h_01GWP89PYY82N9HR4X42R98SKR"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'facetedGeometry':
          return (
            <span>
              Faceted geometry uses flat surfaces without smoothing, which can look unrealistic on curves.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12485385944849#h_01GWP8ABZ92CRXF1804RH0SN3S"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'nonManifoldVertices':
          return (
            <span>
              Manifold geometry ensures all surfaces are properly connected, avoiding issues like edges
              shared by more than two faces.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12485385944849#h_01GWP89YFDSNN5QRRW6W1A8KGB"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        default:
          return null
      }

    case 'texture':
      switch (check) {
        case 'missingRequiredTextures':
          return (
            <span>
              PBR textures simulate how light interacts with materials, making the model look realistic under
            different lighting.
              <br />Required PBR textures:
              <br />- Base Color
              <br />- Roughness
              <br />- Metalness
              <br />- Normal
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12486535401361#h_01GWP80KZMFE5299HB69Q4ZWQ2"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'embeddedTexturesFbx':
          return (
            <span>
              Embedded textures are stored inside the model file, increasing its size and sometimes causing
            compatibility issues.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12486535401361#h_01GWP83GFSK677SPVYTBX5NBQC"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'textureAspectRatio':
          return (
            <span>
              Texture aspect ratio is the width-to-height ratio of a texture. Expected texture aspect ratio: 1:1
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12486535401361#h_01GWP82FF8TT77MNKGXGW6G8KS"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'textureResolutionNotPowerOf2':
          return (
            <span>
              Textures with dimensions in power of two (e.g. 512x512px, 1024x1024px) are used to optimize
            performance and memory usage.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12486535401361#h_01GWP82FF8TT77MNKGXGW6G8KS"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'numberOfMaterialsPerObject':
          return (
            <span>
              Materials are applied to the 3D model to allow visualize a model's surface properties and appearance.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12486535401361#h_01GWP85Y2XHM130GNRYSWF91QE"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        default:
          return null
      }

    case 'uv':
      switch (check) {
        case 'overlappedUvPerUVIsland':
          return (
            <span>
              UVs overlap when multiple points on the 3D model's surface are mapped to the same point on the
            UV island causing texture stretching.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12485759132177#h_01GWP86YVAZRJNZ4PBARMTMWR7"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        case 'numberOfUVs':
          return (
            <span>
              A UV unwrapped model means its 3D surface is flattened into 2D space, allowing textures to be
            applied accurately.
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12485759132177#h_01GWP86PHGJY9H3PARWNABV34J"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        default:
          return null
      }

    case 'naming':
      switch (check) {
        case 'allowedCharacters':
          return (
            <span>
              Allowed ASCII characters: a-zA-Z0-9-_
              <br />
              <a
                href="https://help.cgtrader.com/hc/en-us/articles/12485803270929#h_01GWP88M0S448FKN1SMABDP21R"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </span>
          )
        default:
          return null
      }

    case '3dprint':
      return getGeometryTooltipContent(check, locale)

    default:
      return null
  }
}
